import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveCampaign,
  listCampaigns,
  type CampaignStatus,
  type OutboundCampaign,
} from '../api/campaigns.js';
import { listConnectors } from '../api/connectors.js';
import { listTemplates } from '../api/templates.js';
import { ApiError } from '../api/apiClient.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

const STATUS_TONE: Record<CampaignStatus, 'green' | 'red' | 'slate' | 'yellow'> = {
  draft: 'slate',
  scheduled: 'yellow',
  running: 'yellow',
  completed: 'green',
  archived: 'slate',
};

const STATUSES: CampaignStatus[] = [
  'draft',
  'scheduled',
  'running',
  'completed',
  'archived',
];

export function CampaignsList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');
  const [confirmTarget, setConfirmTarget] = useState<OutboundCampaign | null>(null);
  const pushToast = useUiStore((s) => s.pushToast);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (id && id !== activeTenantId) setActiveTenant(id);
  }, [id, activeTenantId, setActiveTenant]);

  const connectorsQuery = useQuery({
    queryKey: ['connectors', id, { includeArchived: false }],
    queryFn: () => listConnectors(id),
    enabled: Boolean(id),
  });

  const templatesQuery = useQuery({
    queryKey: ['templates', id, { includeArchived: false }],
    queryFn: () => listTemplates(id),
    enabled: Boolean(id),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'campaigns',
      id,
      { includeArchived, status: statusFilter || undefined },
    ],
    queryFn: () =>
      listCampaigns(id, {
        includeArchived,
        status: statusFilter || undefined,
      }),
    enabled: Boolean(id),
  });

  const archiveMutation = useMutation({
    mutationFn: (campaignId: string) => archiveCampaign(id, campaignId),
    onSuccess: () => {
      pushToast('success', t.campaign.archived);
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
      setConfirmTarget(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const campaigns = data ?? [];
  const connectorById = new Map(
    (connectorsQuery.data ?? []).map((c) => [c.id, c]),
  );
  const templateById = new Map(
    (templatesQuery.data ?? []).map((tpl) => [tpl.id, tpl]),
  );

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t.campaign.listTitle}</h2>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600">
            <span className="sr-only">{t.campaign.filterStatus}</span>
            <select
              aria-label={t.campaign.filterStatus}
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as CampaignStatus | '')
              }
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
            >
              <option value="">{t.campaign.filterAll}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t.campaign.status[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t.campaign.showArchived}
          </label>
          <Link
            to={`/tenants/${id}/campaigns/new`}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
          >
            {t.campaign.newBtn}
          </Link>
        </div>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}
      {!isLoading && !error && campaigns.length === 0 && (
        <p className="text-slate-500">{t.campaign.empty}</p>
      )}

      {campaigns.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">{t.campaign.fields.name}</th>
                <th className="px-4 py-2">{t.campaign.fields.connector}</th>
                <th className="px-4 py-2">{t.campaign.fields.template}</th>
                <th className="px-4 py-2">{t.campaign.fields.status}</th>
                <th className="px-4 py-2">{t.campaign.fields.contactsCount}</th>
                <th className="px-4 py-2">{t.campaign.fields.deliveredCount}</th>
                <th className="px-4 py-2">{t.campaign.fields.version}</th>
                <th className="px-4 py-2 text-right">{t.campaign.fields.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {campaigns.map((c) => {
                const conn = connectorById.get(c.connectorId);
                const tpl = templateById.get(c.templateId);
                return (
                  <tr
                    key={c.id}
                    data-testid={`campaign-row-${c.id}`}
                    className={c.deletedAt ? 'opacity-50' : undefined}
                  >
                    <td className="px-4 py-2 font-medium">{c.name}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {conn?.name ?? c.connectorId}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {tpl?.name ?? c.templateId}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge
                        label={t.campaign.status[c.status]}
                        tone={STATUS_TONE[c.status]}
                      />
                    </td>
                    <td className="px-4 py-2 text-slate-600">{c.contactsCount}</td>
                    <td className="px-4 py-2 text-slate-600">{c.deliveredCount}</td>
                    <td className="px-4 py-2 text-slate-500">v{c.version}</td>
                    <td className="px-4 py-2 text-right">
                      {!c.deletedAt && (
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/tenants/${id}/campaigns/${c.id}`}
                            className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                          >
                            {t.campaign.detail}
                          </Link>
                          <Link
                            to={`/tenants/${id}/campaigns/${c.id}/edit`}
                            className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                          >
                            {t.campaign.edit}
                          </Link>
                          <button
                            type="button"
                            onClick={() => setConfirmTarget(c)}
                            className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                          >
                            {t.campaign.archive}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={confirmTarget !== null}
        title={t.campaign.confirmArchiveTitle}
        description={t.campaign.confirmArchiveDesc}
        confirmLabel={t.campaign.archive}
        cancelLabel={t.app.cancel}
        loading={archiveMutation.isPending}
        onConfirm={() => {
          if (confirmTarget) archiveMutation.mutate(confirmTarget.id);
        }}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
